import { loginInputSchema, type LoginInput } from '@hr/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FieldError } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { uk } from '@/i18n/uk';
import { login } from '@/lib/auth';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.query(trpc.auth.me.queryOptions());
    if (me) throw redirect({ to: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

/** Дозволяємо повернення тільки на внутрішні шляхи застосунку. */
function safeRedirect(path: string | undefined): string {
  return path?.startsWith('/') && !path.startsWith('//') ? path : '/';
}

function LoginPage() {
  const router = useRouter();
  const search = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values);
      await router.navigate({ to: safeRedirect(search.redirect) });
    } catch (e) {
      setError(e instanceof Error ? e.message : uk.common.error);
    }
  });

  return (
    <main className="flex min-h-svh items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-sm rounded-xl border bg-card p-8 shadow-sm">
        <div className="mb-6 space-y-1 text-center">
          <img src="/favicon.svg" alt="" className="mx-auto mb-3 size-10" />
          <h1 className="text-xl font-semibold">{uk.login.title}</h1>
          <p className="text-sm text-muted-foreground">{uk.login.subtitle}</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="email">{uk.login.email}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.email)}
              {...form.register('email')}
            />
            <FieldError error={form.formState.errors.email} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{uk.login.password}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
            <FieldError error={form.formState.errors.password} />
          </div>
          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? uk.login.submitting : uk.login.submit}
          </Button>
        </form>
      </div>
    </main>
  );
}
